import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button, Spinner } from 'react-bootstrap';
import CdcrFormPage, { formatCdcrDate, localToday, toDateInputValue } from './CdcrFormPage';
import { getCdcrsByOrderLine, getCdcrPrefill, saveCdcr, deleteCdcr } from '../../context/CommonContex';
import './CdcrForm.css';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

const titleWord = (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/** john.doe@gmail.com -> John Doe. A name that is not an email is left as entered. */
const toOriginatorName = (value) => {
    const raw = String(value || '').trim();
    if (!raw.includes('@')) return raw;
    const local = raw.split('@')[0];
    const parts = local.split(/[._\-\s]+/).filter(Boolean).map(titleWord);
    if (parts.length === 0) return raw;
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1]}`;
};

const withOriginatorName = (form) => (
    form ? { ...form, originatorName: toOriginatorName(form.originatorName) } : form
);

/**
 * Customer Drawing Change Request for one order line (part).
 * - Opens the newest saved CDCR in view mode, or a new prefilled one in edit mode when none exist.
 * - orderLine: the order line row ({ id, ordernum, lineitem, prcpart, part }).
 * - seed: values the screen already knows (e.g. { customerName }) used to fill gaps in the server prefill.
 */
const CdcrModal = ({ show, onClose, orderLine, seed }) => {
    const orderLineId = orderLine?.id;
    const ordernum = orderLine?.ordernum;
    const [cdcrs, setCdcrs] = useState([]);
    const [form, setForm] = useState(null);
    const [savedForm, setSavedForm] = useState(null);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const exportRef = useRef(null);
    // Ref so a new seed object from each parent render doesn't re-trigger loading.
    const seedRef = useRef(seed);
    seedRef.current = seed;

    const startNew = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const prefill = withOriginatorName({ ...(await getCdcrPrefill(orderLineId)) });
            Object.entries(seedRef.current || {}).forEach(([key, val]) => {
                if (!prefill[key] && val) prefill[key] = val;
            });
            // New requests always open on today's local date. The server date can land on yesterday.
            prefill.dateRequested = localToday();
            setForm(prefill);
            setSavedForm(null);
            setEditing(true);
        } catch (err) {
            setError('Could not load order line details for a new CDCR.');
        } finally {
            setLoading(false);
        }
    }, [orderLineId]);

    useEffect(() => {
        if (!show || !orderLineId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            setForm(null);
            try {
                const list = await getCdcrsByOrderLine(orderLineId);
                if (cancelled) return;
                setCdcrs(list || []);
                if (list && list.length > 0) {
                    const opened = withOriginatorName(list[0]);
                    opened.dateRequested = toDateInputValue(opened.dateRequested);
                    setForm(opened);
                    setSavedForm(opened);
                    setEditing(false);
                    setLoading(false);
                } else {
                    await startNew();
                }
            } catch (err) {
                if (!cancelled) {
                    setError('Could not load change requests for this order line.');
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [show, orderLineId, startNew]);

    const isDirty = editing && JSON.stringify(form) !== JSON.stringify(savedForm);

    const confirmDiscard = () => !isDirty || window.confirm('Discard unsaved changes?');

    const handleClose = () => {
        if (confirmDiscard()) onClose();
    };

    const handleSelect = (id) => {
        if (!confirmDiscard()) return;
        const selected = cdcrs.find((c) => String(c.id) === String(id));
        if (selected) {
            const opened = withOriginatorName(selected);
            opened.dateRequested = toDateInputValue(opened.dateRequested);
            setForm(opened);
            setSavedForm(opened);
            setEditing(false);
        }
    };

    const handleNew = () => {
        if (confirmDiscard()) startNew();
    };

    const handleCancelEdit = () => {
        if (!confirmDiscard()) return;
        if (savedForm) {
            setForm(savedForm);
            setEditing(false);
        } else {
            onClose();
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const requestedDate = toDateInputValue(form.dateRequested) || localToday();
            const response = await saveCdcr({ ...form, orderLineId, dateRequested: requestedDate });
            const savedBody = response && typeof response === 'object' && !Array.isArray(response)
                ? { ...form, ...response }
                : { ...form };
            savedBody.dateRequested = requestedDate;
            savedBody.orderLineId = orderLineId;
            const saved = withOriginatorName(savedBody);
            setForm(saved);
            setSavedForm(saved);
            setEditing(false);
            setCdcrs((prev) => {
                const exists = prev.some((c) => c.id === saved.id);
                return exists ? prev.map((c) => (c.id === saved.id ? saved : c)) : [saved, ...prev];
            });
        } catch (err) {
            setError('Saving failed. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!form?.id || !window.confirm(`Delete ${form.cdcrNumber || 'this CDCR'}? This cannot be undone.`)) return;
        try {
            await deleteCdcr(form.id);
            const remaining = cdcrs.filter((c) => c.id !== form.id);
            setCdcrs(remaining);
            if (remaining.length > 0) {
                setForm(remaining[0]);
                setSavedForm(remaining[0]);
                setEditing(false);
            } else {
                startNew();
            }
        } catch (err) {
            setError('Delete failed. Please try again.');
        }
    };

    // The popup shifts the form, so a picture taken inside it never becomes a file.
    // A copy on the page itself is what gets downloaded.
    const handleDownload = async () => {
        setExporting(true);
        setError('');
        try {
            const [{ default: html2canvas }, pdfMod] = await Promise.all([import('html2canvas'), import('jspdf')]);
            const JsPDF = pdfMod.jsPDF || pdfMod.default;

            const page = await new Promise((resolve) => {
                let frames = 0;
                const tick = () => {
                    const node = exportRef.current;
                    if (node && node.offsetWidth > 0 && node.offsetHeight > 0) {
                        resolve(node);
                        return;
                    }
                    frames += 1;
                    if (frames > 45) resolve(null);
                    else requestAnimationFrame(tick);
                };
                requestAnimationFrame(tick);
            });
            if (!page) {
                setError('The form is not ready to download yet.');
                return;
            }

            const images = Array.from(page.querySelectorAll('img'));
            await Promise.all(images.map((img) => (img.complete ? Promise.resolve() : new Promise((done) => {
                img.onload = done;
                img.onerror = done;
            }))));
            await document.fonts?.ready;

            const canvas = await html2canvas(page, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
            if (!canvas.width || !canvas.height) {
                throw new Error('Could not capture the form.');
            }

            const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageHeightPx = Math.max(1, Math.floor(canvas.width * (A4_HEIGHT_MM / A4_WIDTH_MM)));
            let contentHeight = canvas.height;
            const remainder = contentHeight % pageHeightPx;
            if (contentHeight > pageHeightPx && remainder > 0 && remainder < 24) {
                contentHeight -= remainder;
            }

            for (let offset = 0, pageIndex = 0; offset < contentHeight; offset += pageHeightPx, pageIndex += 1) {
                const sliceHeight = Math.min(pageHeightPx, contentHeight - offset);
                if (sliceHeight < 2) break;
                const slice = document.createElement('canvas');
                slice.width = canvas.width;
                slice.height = sliceHeight;
                slice.getContext('2d').drawImage(canvas, 0, offset, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
                if (pageIndex > 0) pdf.addPage();
                const imageHeightMm = (sliceHeight / canvas.width) * A4_WIDTH_MM;
                pdf.addImage(slice.toDataURL('image/png'), 'PNG', 0, 0, A4_WIDTH_MM, Math.min(imageHeightMm, A4_HEIGHT_MM));
            }

            const fileName = (form.cdcrNumber || `CDCR-${ordernum}`).replace(/[^\w.-]+/g, '_');
            const url = URL.createObjectURL(pdf.output('blob'));
            const link = document.createElement('a');
            link.href = url;
            link.download = `${fileName}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch (err) {
            console.error('CDCR PDF export failed:', err);
            setError('PDF download failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const optionLabel = (c) => `${c.cdcrNumber || `CDCR #${c.id}`}${c.dateRequested ? ` · ${formatCdcrDate(c.dateRequested)}` : ''}`;

    return (
        <Modal show={show} onHide={handleClose} centered dialogClassName="cdcr-modal-dialog" enforceFocus={false} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.05rem' }}>
                    Customer Drawing Change Request · {ordernum}
                    {orderLine?.lineitem ? ` · Line ${orderLine.lineitem}` : ''}
                    {orderLine?.prcpart ? ` · ${String(orderLine.prcpart).substring(3)}` : ''}
                </Modal.Title>
            </Modal.Header>

            <div className="cdcr-toolbar">
                <span className="cdcr-toolbar-title">
                    {editing ? (form?.id ? 'Editing' : 'New request') : 'Viewing'}
                </span>
                {cdcrs.length > 1 && !editing && (
                    <select className="form-select form-select-sm" value={form?.id || ''} onChange={(e) => handleSelect(e.target.value)}>
                        {cdcrs.map((c) => <option key={c.id} value={c.id}>{optionLabel(c)}</option>)}
                    </select>
                )}
                {!loading && form && (editing ? (
                    <>
                        <Button size="sm" variant="outline-secondary" onClick={handleCancelEdit} disabled={saving}>Cancel</Button>
                        <Button size="sm" variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : <><i className="fa fa-save" /> Save</>}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button size="sm" variant="outline-secondary" onClick={handleNew}><i className="fa fa-plus" /> New</Button>
                        <Button size="sm" variant="outline-danger" onClick={handleDelete}><i className="fa fa-trash" /></Button>
                        <Button size="sm" variant="outline-primary" onClick={() => setEditing(true)}><i className="fa fa-pencil" /> Edit</Button>
                        <Button size="sm" variant="primary" onClick={handleDownload} disabled={exporting}>
                            {exporting ? <Spinner size="sm" animation="border" /> : <><i className="fa fa-download" /> Download PDF</>}
                        </Button>
                    </>
                ))}
            </div>

            {error && <div className="alert alert-danger m-2 py-2">{error}</div>}

            <div className="cdcr-canvas">
                {loading && <div className="cdcr-status"><Spinner animation="border" size="sm" /> Loading…</div>}
                {!loading && form && (
                    <CdcrFormPage value={form} editable={editing} onChange={setForm} />
                )}
            </div>
            {exporting && form && createPortal(
                <div className="cdcr-export-host" aria-hidden="true">
                    <CdcrFormPage ref={exportRef} value={form} exporting />
                </div>,
                document.body
            )}
        </Modal>
    );
};

export default CdcrModal;
